import {useState, useEffect, useCallback, useRef} from 'react';

function useSessionStorage<T>(
    key: string,
    initialValue: T
): [T, (value: T | ((prevValue: T) => T)) => void] {
    // Held in a ref so readValue depends only on the key. Depending on the
    // VALUE's identity meant a non-primitive literal default (e.g. `[]`)
    // re-created readValue every render, re-firing the sync effect below with
    // a freshly-parsed object — an infinite update loop.
    const initialValueRef = useRef(initialValue);

    // Get from session storage then parse stored json or return the default
    const readValue = useCallback((): T => {
        // Prevent build error "window is undefined" but keep working
        if (typeof window === 'undefined') {
            return initialValueRef.current;
        }

        try {
            const item = window.sessionStorage.getItem(key);
            return item ? (JSON.parse(item) as T) : initialValueRef.current;
        } catch (error) {
            console.warn(`Error reading sessionStorage key "${key}":`, error);
            return initialValueRef.current;
        }
    }, [key]);

    // State to store our value
    // Pass initial state function to useState so logic is only executed once
    const [storedValue, setStoredValue] = useState<T>(readValue);

    // Return a wrapped version of useState's setter function that
    // persists the new value to sessionStorage.
    const setValue = useCallback(
        (value: T | ((prevValue: T) => T)) => {
            try {
                // By using the functional update form of `setStoredValue`,
                // we can get the previous value without needing it in the dependency array.
                setStoredValue(prevStoredValue => {
                    // Allow value to be a function so we have the same API as useState
                    const newValue =
                        value instanceof Function
                            ? value(prevStoredValue)
                            : value;

                    // Save to session storage
                    if (typeof window !== 'undefined') {
                        window.sessionStorage.setItem(
                            key,
                            JSON.stringify(newValue)
                        );
                    }

                    // Return the new value to update the state
                    return newValue;
                });
            } catch (error) {
                console.warn(
                    `Error setting sessionStorage key "${key}":`,
                    error
                );
            }
        },
        [key] // Now `setValue` is stable and only changes if `key` changes.
    );

    // This effect now correctly runs only when the `key` changes,
    // preventing the infinite loop.
    useEffect(() => {
        setStoredValue(readValue());
    }, [readValue]);

    // Subscribe to changes in other tabs/windows
    // Note: 'storage' event usually only fires for localStorage changes in other tabs,
    // sessionStorage is per-tab. However, we can listen for manual dispatch if needed,
    // but for now we follow the pattern.
    useEffect(() => {
        const handleStorageChange = (event: StorageEvent) => {
            if (
                event.storageArea === window.sessionStorage &&
                event.key === key &&
                event.newValue
            ) {
                try {
                    setStoredValue(JSON.parse(event.newValue));
                } catch (error) {
                    console.warn(
                        `Error parsing sessionStorage value for key "${key}":`,
                        error
                    );
                }
            }
        };

        if (typeof window !== 'undefined') {
            window.addEventListener('storage', handleStorageChange);
            return () => {
                window.removeEventListener('storage', handleStorageChange);
            };
        }
    }, [key]);

    return [storedValue, setValue];
}

export default useSessionStorage;
